import React, { useMemo } from "react";
import { ACCOMMODATION_COLORS, formatCategory, isUnclassifiedCategory } from "./shared";
import { SkeletonText } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

const formatAccommodation = (key) => {
  if (!key) return "";
  if (key === "rv") return "RV";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const accommodationColor = (key) => ACCOMMODATION_COLORS[key]?.dot || "#9ca3af";

// The backend GROUPs BY category and emits `null` and the literal string "Undefined"
// as separate rows — both of which formatCategory() collapses to "Unclassified", so
// they'd render as two identical-looking rows. Fold all unclassified rows into one,
// matching CategoryDistributionPanel and the GROUP BY intent. Averages are weighted by
// event_count; top_score is a max; most_common_match comes from the largest contributor.
const mergeUnclassifiedRows = (rows) => {
  const classified = [];
  const unclassifiedParts = [];

  for (const row of rows) {
    if (isUnclassifiedCategory(row.category)) unclassifiedParts.push(row);
    else classified.push(row);
  }

  if (unclassifiedParts.length <= 1) return rows;

  const totalEvents = unclassifiedParts.reduce((sum, row) => sum + Number(row.event_count || 0), 0);
  const weighted = (field) =>
    totalEvents > 0
      ? unclassifiedParts.reduce((sum, row) => sum + Number(row[field] || 0) * Number(row.event_count || 0), 0) / totalEvents
      : 0;
  const dominant = [...unclassifiedParts].sort(
    (rowA, rowB) => Number(rowB.event_count || 0) - Number(rowA.event_count || 0)
  )[0];

  const merged = {
    category: null,
    event_count: totalEvents,
    avg_score: weighted("avg_score"),
    avg_distance: weighted("avg_distance"),
    top_score: unclassifiedParts.reduce((max, row) => Math.max(max, Number(row.top_score || 0)), 0),
    most_common_match: dominant?.most_common_match ?? null,
  };

  return [...classified, merged];
};

const buildInsight = (rows) => {
  const named = rows.filter((row) => !isUnclassifiedCategory(row.category));
  if (named.length === 0) return null;

  const sorted = [...named].sort((rowA, rowB) => Number(rowB.avg_score || 0) - Number(rowA.avg_score || 0));
  const top1 = sorted[0];
  const top2 = sorted[1] || null;
  const worst = sorted[sorted.length - 1];

  const describe = (row) =>
    `${row.category} events (avg score ${Math.round(Number(row.avg_score || 0))}, best match: ${formatAccommodation(row.most_common_match)})`;

  const sentences = [];
  if (top2 && top2 !== worst) {
    sentences.push(`Your property is best positioned for ${describe(top1)} and ${describe(top2)}.`);
  } else {
    sentences.push(`Your property is best positioned for ${describe(top1)}.`);
  }
  if (worst && worst !== top1 && worst !== top2) {
    sentences.push(
      `${worst.category} events score low (avg ${Math.round(Number(worst.avg_score || 0))}) — these aren't marketing targets.`
    );
  }
  return sentences.join(" ");
};

const ScoreBar = ({ row }) => {
  const score = Math.max(0, Math.min(100, Number(row.avg_score || 0)));
  const top = Math.max(0, Math.min(100, Number(row.top_score || 0)));
  const color = accommodationColor(row.most_common_match);
  const label = formatCategory(row.category);
  const events = Number(row.event_count || 0);
  const avgDist = Number(row.avg_distance || 0);
  const match = row.most_common_match;

  const bar = (
    <div className="h-6 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden relative">
      <div
        className="h-full rounded transition-all"
        style={{ width: `${score}%`, backgroundColor: color }}
      />
      {top > score && (
        <div
          className="absolute top-0 h-full w-0.5 bg-gray-600 dark:bg-gray-300 opacity-70"
          style={{ left: `${top}%` }}
          title={`Top score: ${top}`}
        />
      )}
    </div>
  );

  return (
    <div className="flex items-center gap-3 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-2">
      <div className="flex items-center gap-3 max-[499px]:w-full">
        <div className="w-40 shrink-0 min-w-0 max-[499px]:flex-1 max-[499px]:w-auto">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" title={label}>
            {label}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {events} event{events === 1 ? "" : "s"} · {avgDist.toFixed(1)} mi
          </div>
        </div>

        <div className="hidden max-[499px]:flex w-32 shrink-0 text-right items-center justify-end gap-2">
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{Math.round(score)}</span>
          {match && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium capitalize"
              style={{ color }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              {formatAccommodation(match)}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 relative max-[499px]:w-full max-[499px]:flex-none">
        {bar}
      </div>

      <div className="max-[499px]:hidden w-32 shrink-0 text-right flex items-center justify-end gap-2">
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{Math.round(score)}</span>
        {match && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-medium capitalize"
            style={{ color }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {formatAccommodation(match)}
          </span>
        )}
      </div>
    </div>
  );
};

const PropertyEventMatchPanel = ({ loading, error, rows, coverageGaps }) => {
  const sorted = useMemo(() => {
    const matchRows = mergeUnclassifiedRows(Array.isArray(rows) ? [...rows] : []);
    matchRows.sort((rowA, rowB) => Number(rowB.avg_score || 0) - Number(rowA.avg_score || 0));
    return matchRows;
  }, [rows]);

  const insight = useMemo(() => buildInsight(sorted), [sorted]);

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Property Event Match</h3>
            <InfoTip text="Coverage gaps surface event categories that would pair well with your inventory but aren't showing up nearby — useful for spotting underserved demand or where to source more events." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Which event categories are happening near this property and how well they match your accommodation types.
          </p>
        </div>
      </div>

      {loading ? (
        <SkeletonText lines={6} lineClass="h-10" />
      ) : error ? (
        <ErrorState message={error} />
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No scored events yet for any category.
        </div>
      ) : (
        <>
          {(insight || coverageGaps?.length > 0) && (
            <div className="mb-5 bg-[#32B1FF]/5 border border-[#32B1FF]/20 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-[#32B1FF] shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26A7 7 0 0 0 12 2z" />
              </svg>
              <div className="flex-1 min-w-0">
                {insight && (
                  <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
                    {insight}
                  </p>
                )}
                {coverageGaps?.length > 0 && (
                  <div className={`${insight ? "mt-3 pt-3 border-t border-[#32B1FF]/20" : ""}`}>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                      Coverage gaps in high-affinity categories
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {coverageGaps.map((gap) => (
                        <span
                          key={gap.category}
                          className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300"
                          title={`Best affinity ${gap.maxAffinity.toFixed(2)} (${gap.bestAccommodation})`}
                        >
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          {gap.category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {sorted.map((row, index) => (
              <ScoreBar key={`${row.category ?? "null"}-${index}`} row={row} />
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-4">
            <span className="uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 max-[499px]:w-full">Bar color</span>
            {Object.entries(ACCOMMODATION_COLORS).map(([key, cfg]) => (
              <span key={key} className="inline-flex items-center gap-1.5 capitalize">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.dot }} />
                {formatAccommodation(key)}
              </span>
            ))}
            <span className="ml-auto inline-flex items-center gap-1.5 max-[424px]:w-full max-[424px]:ml-0">
              <span className="inline-block w-0.5 h-3 bg-gray-600 dark:bg-gray-300" />
              Top score marker
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyEventMatchPanel;
